import zod from "zod";
import { fileSchema } from "@/api/http/schemas/fileSchemas";
import { offerDocumentSchema } from "@/api/http/schemas/documentsSchemas";
import { offerSchema } from "@/api/http/schemas/offerSchemas";
import { ownerSchema } from "@/api/http/schemas/ownerSchemas";

export const fundingAdvisorSchema = zod.object({
  first_name: zod.string(),
  last_name: zod.string(),
  telephone: zod.string().nullable(),
  calendar_link: zod.string().nullable(),
});

export type FundingAdvisor = zod.infer<typeof fundingAdvisorSchema>;

export const stageBlockSchema = zod.union([
  zod.literal("all_offers"),
  zod.literal("accepted_offer"),
  zod.literal("funding"),
  zod.literal("checkout"),
]);

export type StageBlock = zod.infer<typeof stageBlockSchema>;

export const stageDataSchema = zod.object({
  action_description: zod.string().optional(),
  action_title: zod.string().optional(),
  button_redirect_type: zod
    .union([
      zod.literal("resume_link"),
      zod.literal("redirect_link"),
      zod.literal("calendar_link"),
      zod.null(),
    ])
    .optional(),
  button_text: zod.string().nullable().optional(),
  button_url: zod.string().nullable().optional(),
  icon: zod.string().nullable().optional(),
  is_current: zod.boolean(),
  show_button: zod.boolean().optional(),
  stage_description: zod.string(),
  stage_name: zod.string(),
  blocks: zod.array(stageBlockSchema).optional(),
});

export type StageData = zod.infer<typeof stageDataSchema>;

export enum OfferDisplay {
  Basic = 1,
  Dynamic,
}

const offerDisplaySchema = zod.nativeEnum(OfferDisplay);

export type Owner = zod.infer<typeof ownerSchema>;

export const chatSettingsSchema = zod.object({
  is_enabled: zod.boolean().default(false),
  agent_id: zod.string().nullable().optional(),
  agent_webhook_url: zod.string().nullable().optional(),
});

export type ChatSettings = zod.infer<typeof chatSettingsSchema>;

const offerTemplateSettingSchema = zod.object({
  id: zod.string(),
  weight: zod.number(),
});

const funderSettingSchema = zod.object({
  id: zod.string(),
  offer_templates: zod.array(offerTemplateSettingSchema),
});

export const applicationSchema = zod.object({
  id: zod.string(),
  amount_needed: zod.number().nullable(),
  personal_information_id: zod.number(),
  business_legal_name: zod.string(),
  funding_advisor: fundingAdvisorSchema.nullable(),
  workflow_template_id: zod.string(),
  stages: zod
    .array(stageDataSchema)
    .nullable()
    .transform((val) => val ?? []),
  created_at: zod.string().datetime(),
  offer_declined_reasons: zod.array(zod.string()),
  offer_declined_reasons_other_label: zod.string().nullable(),
  offers: zod.array(offerSchema),
  owners: zod.array(ownerSchema),
  is_dead: zod.boolean(),
  offer_display: offerDisplaySchema,
  is_finished: zod.boolean(),
  is_checkout_enabled: zod.boolean(),
  default_borrower_platform_template_id: zod.string().nullable(),
  required_stips: zod.array(offerDocumentSchema),
  chat: chatSettingsSchema.nullable().optional(),
  funder_setting: zod.array(funderSettingSchema),
});

export type Application = zod.infer<typeof applicationSchema>;

const applicationListItemSchema = zod.object({
  id: zod.string(),
  business_legal_name: zod.string().nullable(),
  workflow_template_id: zod.string(),
  created_at: zod.string().datetime(),
});

export type ApplicationListItem = zod.infer<typeof applicationListItemSchema>;

export const allApplicationsSchema = zod.array(applicationListItemSchema);

export const resumeLinkSchema = zod.object({
  url: zod.string().url(),
});

export type ResumeLink = zod.infer<typeof resumeLinkSchema>;

const consentSchema = zod.object({
  id: zod.string().uuid(),
  type: zod.enum([
    "third_party_data_sharing",
    "application_data_accuracy",
    "credit_check",
    "communication",
  ]),
  is_required: zod.boolean(),
  message: zod.string(),
});

const consentContentSchema = zod.object({
  version: zod.string(),
  message: zod.string(),
  hide_text_in_scroll_area: zod.boolean(),
  consents: zod.array(consentSchema).nullable(),
});

export const consentTemplateSchema = zod.object({
  id: zod.string(),
  name: zod.string(),
  client_id: zod.string().nullable(),
  is_default: zod.boolean(),
  is_lendflow_template: zod.boolean(),
  content: consentContentSchema,
  created_at: zod.string().datetime(),
  updated_at: zod.string().datetime(),
});

export type ConsentTemplate = zod.infer<typeof consentTemplateSchema>;
export type Consent = zod.infer<typeof consentSchema>;

const signatureConsentSchema = zod.object({
  id: zod.string().uuid(),
  checked_date: zod.string().datetime().nullable(),
});

export enum SignatureStatus {
  "Requested" = 1,
  "Signed",
  "Failed",
}

const signedDocumentRecipient = zod.object({
  id: zod.string(),
  role: zod.enum(["signer", "client", "guarantor"]),
  entity: zod.number(),
});

const signedDocumentSchema = zod.object({
  id: zod.number(),
  personal_information_id: zod.number(),
  signature_status: zod.nativeEnum(SignatureStatus),
  last_recipient: zod.number().nullable(),
  recipients: zod.array(signedDocumentRecipient),
  type: zod.enum(["application", "checkout"]),
  source: zod.enum(["docusign", "consent", "file_upload"]),
  consents: zod.array(signatureConsentSchema),
  templateable_type: zod.string(),
  templateable_id: zod.string(),
  files: zod.array(fileSchema),
});

export const signedDocumentsSchema = zod.array(signedDocumentSchema);

export type SignedDocument = zod.infer<typeof signedDocumentSchema>;
export type SignedDocumentConsent = zod.infer<typeof signatureConsentSchema>;
